/**
 * SpriteIcon Component - SVG Sprite-based Icon System
 *
 * Uses SVG sprite technique for 211 hotel-specific icons
 * Sprite will be loaded from backend in production
 *
 * USAGE:
 * <SpriteIcon name="calendar" size={24} />
 * <SpriteIcon name="rollaway" size={16} className="text-primary" />
 *
 * BACKEND INTEGRATION:
 * 1. Backend provides sprite at: GET /api/v1/theme/icon-sprite.svg
 * 2. Sprite contains <symbol id="icon-{name}"> elements
 * 3. This component references them via <use href="#icon-{name}"/>
 *
 * For now, using static sprite from /icon-sprite.svg in public folder
 */

import React, { useEffect, useState } from 'react';

export type SpriteIconName =
  | 'calendar' | 'lightbulb' | 'lock' | 'dollar-sign' | 'layers'
  | 'layout' | 'pillars' | 'grid-squares' | 'exhibition' | 'early-checkin'
  | 'champagne' | 'kids-welcome' | 'wine-glass' | 'flower' | 'chocolate' | 'king-bed' | 'bride'
  | 'fruit' | 'gift' | 'custom-amenity' | 'cake' | 'heart'
  | 'rings' | 'honeymoon' | 'confetti' | 'car' | 'plane'
  | 'bus' | 'rose' | 'candle' | 'aromatherapy' | 'balloon'
  | 'pillow' | 'blanket' | 'bedding' | 'bed' | 'rollaway'
  | 'robe' | 'fridge' | 'baby-bottle' | 'paw' | 'restaurant'
  | 'coffee' | 'cocktail' | 'room-service' | 'breakfast' | 'spa'
  | 'massage' | 'pool' | 'calendar-plus' | 'calendar-range' | 'dumbbell'
  | 'calendar-check' | 'credit-card' | 'user' | 'user-plus' | 'users' | 'settings'
  | 'bell' | 'sparkles' | 'sparkle-clean' | 'zap' | 'sunrise'
  | 'sunset' | 'clock' | 'clock-arrow' | 'key' | 'door'
  | 'arrow-up-circle' | 'suitcase' | 'map-pin' | 'navigation' | 'tag'
  | 'star' | 'percent' | 'ticket' | 'megaphone' | 'building'
  | 'sliders' | 'wifi' | 'history' | 'smile' | 'message-circle'
  | 'search-box' | 'receipt' | 'info' | 'cart' | 'check'
  | 'check-circle' | 'x' | 'x-circle' | 'arrow-left' | 'arrow-right'
  | 'chevron-left' | 'chevron-right' | 'chevron-down' | 'plus' | 'minus'
  | 'edit' | 'trash' | 'mail' | 'phone' | 'globe'
  | 'log-out' | 'shield' | 'shield-check' | 'fingerprint' | 'alert-circle'
  | 'alert-triangle' | 'limited' | 'target' | 'briefcase' | 'beach'
  | 'family' | 'yoga' | 'question-circle' | 'search' | 'columns'
  | 'fireworks' | 'accessibility' | 'service-dog' | 'paw-double' | 'king-bed'
  | 'queen-beds' | 'balcony' | 'sofa' | 'shower' | 'bathtub'
  | 'quiet' | 'high-floor' | 'low-floor' | 'cityscape' | 'garden'
  | 'pool-view' | 'view' | 'broom' | 'do-not-disturb' | 'towel'
  | 'iron' | 'laundry' | 'ice-bucket' | 'safe' | 'theme-park'
  | 'golf' | 'camera' | 'image' | 'binoculars' | 'music'
  | 'shopping' | 'leaf' | 'chef-hat' | 'temple' | 'thermometer'
  | 'snowflake' | 'sun' | 'tv' | 'hairdryer' | 'parking'
  | 'valet' | 'ev-charging' | 'luggage-store' | 'crown' | 'award'
  | 'diamond' | 'gift-card' | 'concierge-bell' | 'qr-code' | 'translate'
  | 'document' | 'clipboard' | 'share' | 'download' | 'upload'
  | 'link' | 'copy' | 'cloud' | 'rain' | 'moon'
  | 'castle' | 'bride' | 'groom' | 'video' | 'tour-360'
  | 'pen' | 'wallet' | 'hourglass' | 'chandelier' | 'dance'
  | 'outdoor' | 'indoor' | 'sign' | 'tropical' | 'play'
  | 'pause' | 'couple' | 'home' | 'monitor' | 'smartphone'
  | 'smartphone-check' | 'tablet-check' | 'monitor-check' | 'inbox' | 'eye' | 'refresh-cw' | 'loader'
  | 'celebration' | 'file-plus' | 'message-square' | 'grip-dots' | 'log-in'
  | 'door-open' | 'paw-print' | 'bed-double' | 'droplets' | 'bath'
  | 'arrow-up' | 'rotate-ccw';

export interface SpriteIconProps {
  name: SpriteIconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
}

/**
 * Hook to load the SVG sprite
 * In production, this would fetch from backend: GET /api/v1/theme/icon-sprite.svg
 */
const useSpriteLoader = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Check if sprite is already loaded
    const existingSprite = document.getElementById('icon-sprite-container');
    if (existingSprite) {
      setLoaded(true);
      return;
    }

    // Load buildId for cache-busting
    fetch('/version.json')
      .then(res => res.json())
      .then(versionData => {
        const buildId = versionData.buildId || Date.now();

        // Load sprite with cache-busting query parameter
        // In production, replace with: fetch(`/api/v1/theme/icon-sprite.svg?v=${buildId}`)
        return fetch(`/icon-sprite.svg?v=${buildId}`);
      })
      .then(res => res.text())
      .then(svgContent => {
        // Inject sprite into document
        const container = document.createElement('div');
        container.id = 'icon-sprite-container';
        container.style.display = 'none';
        container.innerHTML = svgContent;
        document.body.insertBefore(container, document.body.firstChild);
        setLoaded(true);
      })
      .catch(err => {
        console.error('Failed to load icon sprite:', err);

        // Fallback: try loading without version parameter
        fetch('/icon-sprite.svg')
          .then(res => res.text())
          .then(svgContent => {
            const container = document.createElement('div');
            container.id = 'icon-sprite-container';
            container.style.display = 'none';
            container.innerHTML = svgContent;
            document.body.insertBefore(container, document.body.firstChild);
            setLoaded(true);
          })
          .catch(fallbackErr => {
            console.error('Failed to load icon sprite (fallback):', fallbackErr);
          });
      });
  }, []);

  return loaded;
};

const SpriteIcon: React.FC<SpriteIconProps> = ({
  name,
  size = 24,
  className = '',
  strokeWidth = 1.5
}) => {
  const spriteLoaded = useSpriteLoader();

  if (!spriteLoaded) {
    // Return placeholder while loading
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        className={className}
        aria-hidden="true"
      />
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <use href={`#icon-${name}`} />
    </svg>
  );
};

export default SpriteIcon;
